import type { FunctionComponent } from 'react';
import { Button, MenuDropdown, SplitButton } from '@george-labs.com/design-system';
import { useMemo } from 'react';

import { getFeatureConfig } from '@luna/feature-config';

import { buildEnvironmentSwitchUrl, getCurrentFrontendEnvironment } from '../../services';
import { getApiTargetLabel } from './EnvironmentSwitchButtonService';
import styles from './EnvironmentSwitchButton.module.scss';

interface EnvironmentSwitchButtonProps {
    name: string;
    origin: string;
}

export const EnvironmentSwitchButton: FunctionComponent<EnvironmentSwitchButtonProps> = ({
    name,
    origin,
}) => {
    const currentFrontendEnvironment = useMemo(getCurrentFrontendEnvironment, []);
    const availableApiTargets = useMemo(
        () => Object.keys(getFeatureConfig((config) => config.devTools.availableApiTargets)),
        [],
    );

    if (!availableApiTargets.length) {
        return (
            <Button
                className={`my-0 ${styles.simpleButton}`}
                aria-pressed={name === currentFrontendEnvironment}
                variant={Button.VARIANT.SECONDARY}
                href={buildEnvironmentSwitchUrl(origin)}
            >
                {name}
            </Button>
        );
    }

    return (
        <SplitButton
            key={name}
            className="d-inline-flex flex-fill"
            button={
                <Button
                    className="flex-fill"
                    aria-pressed={name === currentFrontendEnvironment}
                    variant={Button.VARIANT.SECONDARY}
                    href={buildEnvironmentSwitchUrl(origin)}
                >
                    {name}
                </Button>
            }
            dropdown={
                <MenuDropdown
                    menuClassName={styles.menuDropdown}
                    items={availableApiTargets.map((apiTarget) => ({
                        value: apiTarget,
                        label: getApiTargetLabel(apiTarget),
                        href: buildEnvironmentSwitchUrl(origin, apiTarget),
                    }))}
                    toggle={
                        <Button
                            aria-pressed={name === currentFrontendEnvironment}
                            className={styles.menuToggle}
                        >
                            Select API target
                        </Button>
                    }
                />
            }
        />
    );
};
